import { useEffect, useState } from 'react';
import { DrawerPage } from '../components/CareMapDrawerPage';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { AppointmentEditor } from './AppointmentEditor';
import { AppointmentsState, IAppointmentTrackingFrom, NEW_APPOINTMENT_ID } from './AppointmentsState';
import { useSubscription } from '@meraki-internal/state';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useToast } from '../components/useToast';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IonButton } from '@ionic/react';
import { Menu } from '../components/Menu';
import { DeleteIcon } from '../theme/Icons';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';

interface IAppointmentEditorDrawerProps {
    appointmentId: string;
    trackingFrom?: IAppointmentTrackingFrom;
}

export class AppointmentEditorDrawer implements IQueryParamDrivenDrawerContentProvider<IAppointmentEditorDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IAppointmentEditorDrawerProps>){}

    mode = 'edit-appt';

    Component = EditAppointmentDrawerView;

    open = (props: IAppointmentEditorDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    buildQueryParams = (props: IAppointmentEditorDrawerProps) => {
        return this.drawerHelper.buildQueryParams({ mode: this.mode, appointmentId: props.appointmentId });
    };
}

const EditAppointmentDrawerView: React.FC<IAppointmentEditorDrawerProps> = ({ appointmentId }) => {
    const mixpanel = useInstance(MixPanelEventEmitter);
    const history = useInstance(HistoryViewModel);

    const [saving, setSaving] = useState(false);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const [showToast] = useToast();

    const appointmentsState = useInstance(AppointmentsState);
    useSubscription(() => appointmentsState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const isNew = appointmentId === NEW_APPOINTMENT_ID;

    useEffect(() => {
        if (!isNew) {
            mixpanel.track('Calendar Event Opened', () => ({
                // TODO: could be opened from Home card now...
                'Opened From': 'appointments-page',
                'Association': appointmentsState.getTrackingMeta().association
            }));
        }
    }, [appointmentId, mixpanel, appointmentsState, isNew]);

    const [isValid, setIsValid] = useState<boolean>(false);

    if (!appointmentsState.state.loaded || !careMapState.getActiveCaremap()) {
        return null;
    }

    const canAddOrEdit = () => {
        return appointmentsState.canAdd() || appointmentsState.canEdit();
    };

    const canDelete = () => {
        return appointmentsState.canDelete();
    };

    const deleteAppointment = async () => {
        try {
            setSaving(true);
            await appointmentsState.deleteCurrentAppointment('appointments-page');
            drawerHelper.close();
        } catch (err: any) {
            setSaving(false);
            showToast({ message: err.displayMessage || 'Sorry something went wrong', duration: 3000 });
        }
    };

    const isOnAppointmentsPage = () => {
        const { pathname } = history.getCurrentLocation();
        return pathname.startsWith('/appointments/');
    };

    const save = async () => {
        try {
            setSaving(true);
            const { trackingFrom } = drawerHelper.getDrawerParams();
            await appointmentsState.saveCurrentAppointment(trackingFrom || 'appointments-page');
            drawerHelper.close();

            if (!isOnAppointmentsPage()){
                // wait for modal to close
                setTimeout(() => {
                    showToast({
                        duration: 3000,
                        message: 'Appointment Added!',
                        buttons: [{
                            text: 'View Appointments',
                            handler: () => {
                                history.push(`/appointments/${appointmentsState.getCareMapId()}`);
                            },
                        }]
                    });
                }, 350);
            }
        } catch (err: any) {
            setSaving(false);
            showToast({ message: err.displayMessage || 'Sorry something went wrong', duration: 3000 });
        }
    };

    const cancel = () => {
        appointmentsState.clearCurrentAppointment();
        drawerHelper.close();
    };

    const buttons = canAddOrEdit() ? <>
        <IonButton data-type="save-button" disabled={!isValid || saving} onClick={save}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={cancel}>Cancel</IonButton>
    </> : <>
        <div data-type="cannot-save" />
    </>;

    return (
        <DrawerPage
            hasBack
            buttons={buttons}
            onClose={cancel}
            data-type="appointment-editor"
            title={`${isNew ? 'Add' : 'Edit'} Appointment`}
        >
            <>
                {(canDelete() && !isNew) ? (
                    <div style={{marginTop: -8, display: 'flex', justifyContent: 'end'}}>
                        <Menu actions={[{
                            Icon: DeleteIcon,
                            role: 'destructive',
                            text: `Delete Appointment`,
                            handler: deleteAppointment,
                            'data-type': 'delete-button',
                            disabled: saving
                        }]} />
                    </div>
                ) : <div data-type="no-menu" />}

                <AppointmentEditor
                    readOnly={!canAddOrEdit()}
                    appointmentId={appointmentId}
                    reportValidity={(valid) => setIsValid(valid)}
                />
            </>
        </DrawerPage>
    );
};
